import type { ExportListParams, ExportSummary } from '@/lib/api/reports-client.js';
import { useMemo, useState } from 'react';
import { useCancelExport } from '../hooks/use-cancel-export.js';
import { useDownloadExport } from '../hooks/use-download-export.js';
import { useExportStatusPolling } from '../hooks/use-export-status-polling.js';
import { useExportsList } from '../hooks/use-exports-list.js';
import { ExportsEmptyState } from './exports-empty-state.js';
import { ExportsErrorState } from './exports-error-state.js';
import { ExportsFilters } from './exports-filters.js';
import { ExportsTable, ExportsTableSkeleton } from './exports-table.js';

export interface ExportsSearchParams {
  status?: ExportSummary['status'];
  format?: 'csv' | 'xlsx' | 'pdf';
  pageNumber?: number;
  pageSize?: number;
}

interface ExportsPageProps {
  searchParams: ExportsSearchParams;
  onSearchParamsChange: (next: ExportsSearchParams) => void;
}

export function ExportsPage({ searchParams, onSearchParamsChange }: ExportsPageProps) {
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  // Build wire params
  const listParams: ExportListParams = useMemo(() => {
    const p: ExportListParams = {};
    if (searchParams.status !== undefined) p.status = searchParams.status;
    if (searchParams.format !== undefined) p.format = searchParams.format;
    if (searchParams.pageNumber !== undefined) p.page_number = searchParams.pageNumber;
    if (searchParams.pageSize !== undefined) p.page_size = searchParams.pageSize;
    return p;
  }, [searchParams]);

  const { data, isLoading, isError, error, refetch, isFetching } = useExportsList(listParams);

  // Collect in-flight IDs for polling
  const inFlightIds = useMemo(
    () =>
      (data?.items ?? [])
        .filter((i) => i.status === 'queued' || i.status === 'running')
        .map((i) => i.exportId),
    [data?.items],
  );

  useExportStatusPolling(inFlightIds);

  const cancelMutation = useCancelExport();
  const { download } = useDownloadExport();

  function handleCancel(exportId: string) {
    setCancellingIds((prev) => new Set([...prev, exportId]));
    cancelMutation.mutate(exportId, {
      onSettled: () => {
        setCancellingIds((prev) => {
          const next = new Set(prev);
          next.delete(exportId);
          return next;
        });
      },
    });
  }

  function handleDownload(exportId: string) {
    void download(exportId);
  }

  function handleFiltersChange(next: ExportListParams) {
    const nextSearch: ExportsSearchParams = {};
    if (next.status !== undefined) nextSearch.status = next.status;
    if (next.format !== undefined) nextSearch.format = next.format;
    if (next.page_number !== undefined) nextSearch.pageNumber = next.page_number;
    if (next.page_size !== undefined) nextSearch.pageSize = next.page_size;
    onSearchParamsChange(nextSearch);
  }

  function handlePageChange(page: number) {
    onSearchParamsChange({ ...searchParams, pageNumber: page });
  }

  const filterParams: ExportListParams = {};
  if (searchParams.status !== undefined) filterParams.status = searchParams.status;
  if (searchParams.format !== undefined) filterParams.format = searchParams.format;
  if (searchParams.pageNumber !== undefined) filterParams.page_number = searchParams.pageNumber;
  if (searchParams.pageSize !== undefined) filterParams.page_size = searchParams.pageSize;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-qup-text-dark)]">Exportaciones</h1>
          <p className="mt-0.5 text-xs text-[var(--color-qup-text-muted-dark)]">Últimos 30 días</p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          aria-label="Actualizar"
          className="flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)] px-3 py-1.5 text-xs text-[var(--color-qup-text-dark)] transition-colors hover:border-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary)] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
              clipRule="evenodd"
            />
          </svg>
          Actualizar
        </button>
      </div>

      {/* Filters */}
      <ExportsFilters params={filterParams} onParamsChange={handleFiltersChange} />

      {/* Content */}
      {isLoading && <ExportsTableSkeleton />}

      {isError && (
        <ExportsErrorState
          message={error?.message ?? 'Error al cargar las exportaciones.'}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
        <ExportsEmptyState message="No hay exportaciones" />
      )}

      {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
        <ExportsTable
          data={data}
          cancellingIds={cancellingIds}
          onDownload={handleDownload}
          onCancel={handleCancel}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}
